import React, { useState, useEffect } from 'react';
import { CheckCircle, Truck, Shield, Star, Phone, Clock, Award, ChevronLeft, ChevronRight, TrendingUp, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useStore } from '../stores/appStore';

interface Depoimento {
  id: string;
  nome: string;
  cidade: string;
  moto: string;
  avaliacao: number;
  comentario: string;
  data: string;
}

export const Financing: React.FC = () => {
  const { motos, setMotos } = useStore();
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [currentImages, setCurrentImages] = useState<{[key: string]: number}>({});

  // Carregar motos inicialmente
  useEffect(() => {
    // Verificar se já há motos no store
    if (motos.length === 0) {
      // Carregar motos do localStorage ou usar motos padrão
      const savedMotos = localStorage.getItem('motos');
      if (savedMotos) {
        setMotos(JSON.parse(savedMotos));
      }
    }
  }, [motos.length, setMotos]);

  // Transformar motos do store para o formato de financiamento
  const motosFinanciamento = motos.length > 0 ? motos.map(moto => ({
    id: moto.id,
    nome: moto.nome,
    precoOriginal: moto.preco,
    precoPromocional: moto.precoPromocional || moto.preco * 0.3, // 70% OFF se não tiver preço promocional
    economia: moto.preco - (moto.precoPromocional || moto.preco * 0.3),
    imagens: moto.imagens.length > 0 ? moto.imagens : [
      `https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(moto.nome)}%20motorcycle%20professional%20photo&image_size=landscape_16_9`
    ]
  })) : [];

  const depoimentos: Depoimento[] = [
    {
      id: '1',
      nome: 'Carlos Eduardo',
      cidade: 'Rio de Janeiro - RJ',
      moto: 'Honda CG 160',
      avaliacao: 5,
      comentario: 'Superou todas as minhas expectativas! A moto chegou em perfeitas condições e só paguei após conferir tudo. Atendimento nota 10!',
      data: '2025-01-15'
    },
    {
      id: '2',
      nome: 'Fernanda Silva',
      cidade: 'Porto Alegre - RS',
      moto: 'Honda Biz 125',
      avaliacao: 5,
      comentario: 'Excelente experiência! O frete foi super rápido e a equipe muito atenciosa. Recomendo demais!',
      data: '2025-01-12'
    },
    {
      id: '3',
      nome: 'Roberto Santos',
      cidade: 'Salvador - BA',
      moto: 'Honda Elite 125',
      avaliacao: 5,
      comentario: 'Processo simples e seguro. A moto chegou antes do prazo e em ótimas condições. Valeu muito a pena!',
      data: '2025-01-10'
    },
    {
      id: '4',
      nome: 'Juliana Oliveira',
      cidade: 'Belo Horizonte - MG',
      moto: 'Honda PCX 160',
      avaliacao: 5,
      comentario: 'Desde a compra até a entrega, tudo perfeito. Só paguei quando recebi e adorei a moto!',
      data: '2025-01-08'
    },
    {
      id: '5',
      nome: 'Marcos Pereira',
      cidade: 'Recife - PE',
      moto: 'Honda CG 160',
      avaliacao: 5,
      comentario: 'Melhor negócio que fiz! Qualidade impecável, entrega rápida e paguei só depois de receber. Nota 1000!',
      data: '2025-01-06'
    },
    {
      id: '6',
      nome: 'Patricia Costa',
      cidade: 'Curitiba - PR',
      moto: 'Honda CB 250 Twister',
      avaliacao: 5,
      comentario: 'Produto de qualidade e atendimento diferenciado. A segurança de pagar só após receber é fundamental!',
      data: '2025-01-04'
    },
    {
      id: '7',
      nome: 'Rafael Mendes',
      cidade: 'Brasília - DF',
      moto: 'Honda Biz 125',
      avaliacao: 5,
      comentario: 'Compra excelente! Processo transparente, entrega rápida e moto em perfeito estado. Super recomendo!',
      data: '2025-01-02'
    },
    {
      id: '8',
      nome: 'Amanda Rocha',
      cidade: 'Fortaleza - CE',
      moto: 'Honda XRE 190',
      avaliacao: 5,
      comentario: 'Tudo ocorreu como combinado. Recebi em casa e só então finalizei o pagamento. Sensacional!',
      data: '2025-01-18'
    },
    {
      id: '9',
      nome: 'Gustavo Lima',
      cidade: 'Goiânia - GO',
      moto: 'Honda CG 160',
      avaliacao: 5,
      comentario: 'Equipe muito atenciosa e entrega rápida. Moto perfeita, paguei depois de conferir sem stress!',
      data: '2025-01-20'
    },
    {
      id: '10',
      nome: 'Camila Duarte',
      cidade: 'Florianópolis - SC',
      moto: 'Honda PCX 160',
      avaliacao: 5,
      comentario: 'A confiança de pagar após receber fez toda diferença. Recomendo a todos!',
      data: '2025-01-22'
    },
    {
      id: '11',
      nome: 'João Pedro',
      cidade: 'Manaus - AM',
      moto: 'Honda XRE 300',
      avaliacao: 5,
      comentario: 'Entrega no prazo, comunicação clara e moto impecável. Valeu muito a pena!',
      data: '2025-01-24'
    },
    {
      id: '12',
      nome: 'Larissa Martins',
      cidade: 'Belém - PA',
      moto: 'Honda CB 250 Twister',
      avaliacao: 5,
      comentario: 'Atendimento excelente, frete grátis e pagamento só após receber. Experiência perfeita!',
      data: '2025-01-26'
    }
  ];

  // Motos padrão caso o store esteja vazio
  const motosPadrao = [
    { 
      id: '1',
      nome: 'Honda CG 160', 
      precoOriginal: 12000, 
      precoPromocional: 3600, 
      economia: 8400,
      imagens: [
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20CG%20160%202024%20motorcycle%20blue%20color%20professional%20photo%20showroom&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20CG%20160%20side%20view%20detailed%20motorcycle%20photo%20studio%20lighting&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20CG%20160%20engine%20close-up%20detailed%20motorcycle%20parts&image_size=square'
      ]
    },
    { 
      id: '2',
      nome: 'Honda Pop 110', 
      precoOriginal: 8500, 
      precoPromocional: 2550, 
      economia: 5950,
      imagens: [
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Pop%20110%202024%20motorcycle%20red%20color%20professional%20photo&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Pop%20110%20compact%20motorcycle%20side%20view%20studio&image_size=landscape_16_9'
      ]
    },
    { 
      id: '3',
      nome: 'Honda Elite 125', 
      precoOriginal: 10500, 
      precoPromocional: 3150, 
      economia: 7350,
      imagens: [
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Elite%20125%20scooter%20white%20color%20elegant%20design%202024&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Elite%20125%20scooter%20dashboard%20detailed%20view&image_size=landscape_16_9'
      ]
    },
    { 
      id: '4',
      nome: 'Honda PCX 160', 
      precoOriginal: 15000, 
      precoPromocional: 4500, 
      economia: 10500,
      imagens: [
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20PCX%20160%20premium%20scooter%20black%20metallic%202024&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20PCX%20160%20sport%20scooter%20LED%20lights%20detailed&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20PCX%20160%20digital%20panel%20modern%20scooter&image_size=square'
      ]
    },
    { 
      id: '5',
      nome: 'Honda CG 125', 
      precoOriginal: 10000, 
      precoPromocional: 3000, 
      economia: 7000,
      imagens: [
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20CG%20125%20classic%20motorcycle%20blue%20color%202024&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20CG%20125%20traditional%20motorcycle%20side%20view&image_size=landscape_16_9'
      ]
    },
    { 
      id: '6',
      nome: 'Honda Lead 110', 
      precoOriginal: 9000, 
      precoPromocional: 2700, 
      economia: 6300,
      imagens: [
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Lead%20110%20scooter%20silver%20color%20practical%20design&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Lead%20110%20scooter%20storage%20space%20detailed&image_size=landscape_16_9'
      ]
    },
    { 
      id: '7',
      nome: 'Honda Biz 125', 
      precoOriginal: 9500, 
      precoPromocional: 2850, 
      economia: 6650,
      imagens: [
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Biz%20125%20scooter%20red%20color%20youthful%20design%202024&image_size=landscape_16_9',
        'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Honda%20Biz%20125%20scooter%20sporty%20design%20detailed%20view&image_size=landscape_16_9'
      ]
    }
  ];

  const renderStars = (avaliacao: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < avaliacao ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ));
  };

  const handleWhatsAppClick = (motoNome: string) => {
    const mensagem = `Olá! Tenho interesse na ${motoNome}. Vi a promoção de 70% OFF e quero saber mais detalhes sobre como funciona o pagamento após receber. Aguardo contato!`;
    const whatsappUrl = `https://wa.me/5513996763208?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
  };

  const nextImage = (motoId: string, totalImages: number) => {
    setCurrentImages(prev => ({
      ...prev,
      [motoId]: ((prev[motoId] || 0) + 1) % totalImages
    }));
  };

  const prevImage = (motoId: string, totalImages: number) => {
    setCurrentImages(prev => ({
      ...prev,
      [motoId]: ((prev[motoId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  useEffect(() => {
    // WhatsApp button appears after 5 seconds with smooth transition
    const timer = setTimeout(() => {
      setShowWhatsApp(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Motos Honda com 70% OFF - Pagamento só após receber | Vinhedo</title>
        <meta name="description" content="Motos Honda com até 70% OFF em Vinhedo. Pagamento só após receber. Frete grátis para todo território nacional. Entrega em até 48h. Garantia total." />
        <meta name="keywords" content="motos honda, 70% off, venda de motos, vinhedo, pagamento após receber, frete grátis" />
        <meta property="og:title" content="Motos Honda com 70% OFF - Vinhedo" />
        <meta property="og:description" content="Aproveite motos Honda com até 70% OFF. Pagamento só após receber." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://vendamotoshonda.com.br/financiamento" />
      </Helmet>
      {/* Hero Section com Destaque de 70% OFF */}
      <section 
        className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-emerald-800 text-white overflow-hidden"
        role="banner"
        aria-label="Promoção de motos Honda com 70% OFF"
      >
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative container-custom py-16 lg:py-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8 animate-fade-in">
              <span 
                className="inline-block bg-yellow-400 text-yellow-900 px-6 py-2 rounded-full font-bold text-lg mb-4 animate-pulse"
                role="alert"
                aria-live="polite"
              >
                🔥 OFERTA POR TEMPO LIMITADO
              </span>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                Motos Honda com até
                <span className="block text-yellow-400 text-5xl lg:text-7xl animate-bounce">
                  70% OFF
                </span>
              </h1>
              <p className="text-xl lg:text-2xl mb-8 opacity-90">
                Aproveite esta oportunidade única! Estoque limitado.
              </p>
            </div>

            {/* Selo Principal - Pagamento Após Recebimento */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 mb-8 border-2 border-white border-opacity-30">
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
                <div className="bg-emerald-400 text-emerald-900 p-6 rounded-full">
                  <Shield size={48} />
                </div>
                <div className="text-center md:text-left">
                  <h2 className="text-3xl font-bold mb-3 text-yellow-300">
                    🛡️ VOCÊ SÓ PAGA DEPOIS DE RECEBER!
                  </h2>
                  <p className="text-lg lg:text-xl mb-4 opacity-90">
                    Entregamos sua moto em casa em todo o Brasil! 
                    <span className="font-bold text-emerald-300">Você só paga após conferir tudo!</span>
                  </p>
                  <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-emerald-300" size={20} />
                      <span>100% Seguro</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-emerald-300" size={20} />
                      <span>Sem risco</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-emerald-300" size={20} />
                      <span>Garantia total</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Selo de Frete Grátis */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-4 mb-8 inline-block">
              <div className="flex items-center space-x-3">
                <Truck size={32} className="text-white" />
                <div className="text-white">
                  <h3 className="font-bold text-lg">FRETE GRÁTIS</h3>
                  <p className="text-sm">Para Todo o Território Nacional</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção de Motos com Preços */}
      <section className="py-16 lg:py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Escolha Sua Moto com 70% OFF
            </h2>
            <p className="text-lg text-gray-600">
              Estoque limitado - Aproveite agora mesmo!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(motosFinanciamento.length > 0 ? motosFinanciamento : motosPadrao).map((moto, index) => (
              <article key={index} className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative">
                {/* Badges especiais */}
                <div className="absolute -top-3 left-3 flex gap-2 z-10">
                  {((['cg160','biz125'].includes(moto.id)) || /CG\s*160|Biz\s*125/i.test(moto.nome)) && (
                    <span className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold" aria-label="Mais vendidas">
                      <TrendingUp size={14} /> Mais vendidas
                    </span>
                  )}
                  {((moto.id === 'xre300') || /XRE\s*300/i.test(moto.nome)) && (
                    <span className="flex items-center gap-1 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold" aria-label="Maior desconto">
                      <Percent size={14} /> Maior desconto
                    </span>
                  )}
                </div>
                {/* Badge de Desconto */}
                <div className="absolute -top-3 -right-3 bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm transform rotate-12 z-10" aria-label="70% de desconto">
                  -70%
                </div>
                
                {/* Galeria de Imagens */}
                <div className="relative mb-6 h-48 rounded-xl overflow-hidden bg-gray-100">
                  <img
                    src={moto.imagens[currentImages[moto.id] || 0]}
                    alt={`${moto.nome} - Imagem ${(currentImages[moto.id] || 0) + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  
                  {/* Navegação de Imagens */}
                  {moto.imagens.length > 1 && (
                    <>
                      <button
                        onClick={() => prevImage(moto.id, moto.imagens.length)}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                        aria-label="Imagem anterior"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        onClick={() => nextImage(moto.id, moto.imagens.length)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                        aria-label="Próxima imagem"
                      >
                        <ChevronRight size={20} />
                      </button>
                      
                      {/* Indicadores de Imagem */}
                      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                        {moto.imagens.map((_, imgIndex) => (
                          <button
                            key={imgIndex}
                            onClick={() => setCurrentImages(prev => ({ ...prev, [moto.id]: imgIndex }))}
                            className={`w-2 h-2 rounded-full transition-all ${
                              (currentImages[moto.id] || 0) === imgIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                            }`}
                            aria-label={`Ir para imagem ${imgIndex + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{moto.nome}</h3>
                  
                  {/* Preços com destaque */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 line-through mb-1">
                      De: R$ {moto.precoOriginal.toLocaleString('pt-BR')}
                    </p>
                    <p className="text-3xl font-bold text-emerald-600 mb-2">
                      Por: R$ {moto.precoPromocional.toLocaleString('pt-BR')}
                    </p>
                    <p className="text-sm text-emerald-600 font-semibold">
                      Economia de R$ {moto.economia.toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>

                {/* Benefícios */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <CheckCircle className="text-emerald-500" size={16} />
                    <span>Pagamento apenas após receber</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <CheckCircle className="text-emerald-500" size={16} />
                    <span>Frete grátis para todo o Brasil</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <CheckCircle className="text-emerald-500" size={16} />
                    <span>Garantia total de 1 ano</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <CheckCircle className="text-emerald-500" size={16} />
                    <span>Entrega em até 48h</span>
                  </div>
                </div>

                {/* Botão de Ação */}
                <button
                  onClick={() => handleWhatsAppClick(moto.nome)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
                  aria-label={`Comprar ${moto.nome} com 70% de desconto por apenas R$ ${moto.precoPromocional.toLocaleString('pt-BR')}`}
                >
                  Quero Esta Moto!
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Depoimentos de Clientes */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-blue-50 to-emerald-50" aria-labelledby="depoimentos-titulo">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 id="depoimentos-titulo" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Veja o Que Nossos Clientes Dizem
            </h2>
            <p className="text-lg text-gray-600">
              Milhares de clientes satisfeitos em Vinhedo e região
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {depoimentos.map((depoimento) => (
              <article key={depoimento.id} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center mb-4" aria-label={`Avaliação: ${depoimento.avaliacao} de 5 estrelas`}>
                  <div className="flex">
                    {renderStars(depoimento.avaliacao)}
                  </div>
                  <time className="ml-2 text-sm text-gray-500" dateTime={depoimento.data}>
                    {new Date(depoimento.data).toLocaleDateString('pt-BR')}
                  </time>
                </div>
                
                <blockquote className="text-gray-700 mb-4 italic">
                  "{depoimento.comentario}"
                </blockquote>
                
                <footer className="border-t pt-4">
                  <cite className="font-semibold text-gray-900 not-italic">{depoimento.nome}</cite>
                  <p className="text-sm text-gray-600">{depoimento.cidade}</p>
                  <p className="text-sm text-blue-600 font-medium">{depoimento.moto}</p>
                </footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Selos de Segurança e Garantia */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="bg-emerald-100 p-4 rounded-full mb-4">
                <Shield className="text-emerald-600" size={32} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">100% Seguro</h3>
              <p className="text-sm text-gray-600">Pagamento só após receber</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-blue-100 p-4 rounded-full mb-4">
                <Award className="text-blue-600" size={32} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Garantia Total</h3>
              <p className="text-sm text-gray-600">1 ano de fábrica</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-yellow-100 p-4 rounded-full mb-4">
                <Truck className="text-yellow-600" size={32} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Frete Grátis</h3>
              <p className="text-sm text-gray-600">Todo território nacional</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-purple-100 p-4 rounded-full mb-4">
                <Clock className="text-purple-600" size={32} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Entrega Rápida</h3>
              <p className="text-sm text-gray-600">Até 48h úteis</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Final */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-emerald-800 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Não Perca Esta Oportunidade Única!
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Estoque limitado - Promoção por tempo limitado
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={() => handleWhatsAppClick('Honda CG 160')}
              className="bg-yellow-400 text-yellow-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-yellow-300 transition-colors shadow-lg"
            >
              Comprar Agora com 70% OFF
            </button>
            <a
              href="https://wa.me/5513996763208"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Falar no WhatsApp
            </a>
          </div>

          <p className="text-sm opacity-75">
            📍 Atendimento em Vinhedo e toda região • Entrega em até 48h
          </p>
        </div>
      </section>

      {/* WhatsApp Float Button */}
      {showWhatsApp && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in-up">
          <div className="relative group">
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="bg-gray-800 text-white text-sm px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
                Fale conosco no WhatsApp
                <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
            
            {/* Button */}
            <a
              href="https://wa.me/5513996763208"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-green-500/25 flex items-center justify-center"
              aria-label="Falar no WhatsApp"
            >
              <Phone size={32} className="animate-pulse" />
            </a>
            
            {/* Pulse animation ring */}
            <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20"></div>
          </div>
        </div>
      )}
    </div>
  );
};
